import { resolveMediaUrl } from '@/config/env'
import type { StoredMapIconEntry } from '@/models/interfaces/map/MapIconCatalog'

const STORAGE_KEY = 'sigcom_map_icon_catalog_v1'
const MAX_ENTRIES = 60

function generateEntryId(): string {
  return `map_icon_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`
}

export function loadMapIconCatalog(): StoredMapIconEntry[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (e): e is StoredMapIconEntry =>
        e != null &&
        typeof e === 'object' &&
        typeof (e as StoredMapIconEntry).contentUrl === 'string' &&
        typeof (e as StoredMapIconEntry).name === 'string',
    )
  } catch {
    return []
  }
}

export function saveMapIconCatalog(entries: StoredMapIconEntry[]): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)))
  } catch {
    // ignore quota errors
  }
}

export function addMapIconCatalogEntry(partial: Omit<StoredMapIconEntry, 'id' | 'addedAt'> & { id?: string }): StoredMapIconEntry {
  const entries = loadMapIconCatalog()
  const entry: StoredMapIconEntry = {
    id: partial.id ?? generateEntryId(),
    contentUrl: partial.contentUrl,
    name: partial.name,
    addedAt: new Date().toISOString(),
  }
  const withoutDup = entries.filter((e) => e.contentUrl !== entry.contentUrl)
  saveMapIconCatalog([entry, ...withoutDup].slice(0, MAX_ENTRIES))
  return entry
}

export function removeMapIconCatalogEntry(entryId: string): void {
  const entries = loadMapIconCatalog().filter((e) => e.id !== entryId)
  saveMapIconCatalog(entries)
}

/**
 * Session-only cache: contentUrl → blob URL created from the uploaded File.
 * Blob URLs are same-origin and bypass CORS restrictions for map.loadImage().
 * They are invalidated when the page is closed/refreshed (requires backend
 * CORS headers on /media/* for cross-session use).
 */
const blobUrlCache = new Map<string, string>()

/**
 * Normalise any icon URL (relative or absolute) to its absolute form so the
 * cache key is consistent regardless of how the caller obtained the URL.
 */
function normaliseIconCacheKey(url: string): string {
  return resolveMediaUrl(url)
}

/**
 * Register a blob URL for a given contentUrl (call immediately after upload
 * while the File object is still available).
 * Accepts both relative (/media/...) and absolute URLs as key — always stores
 * under the absolute form so lookups are consistent.
 * Revokes any previously registered blob URL for the same key.
 */
export function registerIconBlobUrl(contentUrl: string, file: File): string {
  const key = normaliseIconCacheKey(contentUrl)
  const existing = blobUrlCache.get(key)
  if (existing) {
    URL.revokeObjectURL(existing)
  }
  const blobUrl = URL.createObjectURL(file)
  blobUrlCache.set(key, blobUrl)
  return blobUrl
}

/**
 * Extracts the path component from an absolute media URL (e.g. /media/foo.png).
 * Returns null when the URL does not contain a /media/ segment.
 */
function extractMediaRelativePath(absoluteUrl: string): string | null {
  const match = absoluteUrl.match(/(\/media\/.+)$/)
  return match ? match[1] : null
}

/**
 * Returns the best URL for map.loadImage():
 * 1. Session blob URL (upload within the same page-load — avoids CORS entirely).
 * 2. In development, a Vite-proxy relative path (/media/…) — the Vite dev server
 *    forwards the request to VITE_API_URL, making it same-origin for the browser.
 * 3. Absolute server URL — requires backend CORS headers on /media/* (production fix).
 */
export function resolveIconLoadUrl(contentUrl: string): string {
  const key = normaliseIconCacheKey(contentUrl)

  const blobUrl = blobUrlCache.get(key)
  if (blobUrl) return blobUrl

  // Dev-only: rewrite to a relative path so the Vite proxy forwards the request
  // same-origin, bypassing the CORS restriction on cross-origin media files.
  if (import.meta.env.DEV) {
    const relativePath = extractMediaRelativePath(key)
    if (relativePath) return relativePath
  }

  return key
}

/** Deterministic MapLibre image id for a resolved absolute icon URL. */
export function stableMapIconImageId(absoluteUrl: string): string {
  let hash = 0
  const s = absoluteUrl
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash << 5) - hash + s.charCodeAt(i)
    hash |= 0
  }
  return `sigcom_map_icon_${Math.abs(hash).toString(36)}`
}
